import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/constants";
import { LogOut, LayoutDashboard, Search as SearchIcon, Info } from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user || (user.role !== "client" && user.role !== "professional")) { setUnread(0); return; }
    const poll = () => api.get("/messages/unread-count").then(({ data }) => setUnread(data.unread)).catch(() => {});
    poll();
    const t = setInterval(poll, 30000);
    return () => clearInterval(t);
  }, [user, pathname]);

  const NavLink = ({ to, icon: Icon, label, testId, badge }) => (
    <Link
      to={to}
      data-testid={testId}
      className={`relative flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
        pathname === to ? "text-[#9b111e]" : "text-slate-600 hover:text-[#9b111e]"
      }`}
    >
      <Icon className="w-4 h-4" /> {label}
      {badge > 0 && (
        <span data-testid="nav-unread-badge"
          className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#9b111e] text-white text-[10px] font-black flex items-center justify-center">
          {badge > 9 ? "9+" : badge}
        </span>
      )}
    </Link>
  );

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
          <img src={LOGO_URL} alt="Care-4U" className="h-11 w-11 object-contain drop-shadow" />
          <span className="font-heading font-black text-xl tracking-tight text-slate-900">
            CARE-<span className="text-[#9b111e]">4U</span>
            <span className="text-slate-400 text-sm">.net</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          <NavLink to="/search" icon={SearchIcon} label="Find Services" testId="nav-search" />
          <NavLink to="/about" icon={Info} label="About" testId="nav-about" />
          {user && (
            <NavLink to="/dashboard" icon={LayoutDashboard} label="Dashboard" testId="nav-dashboard" badge={unread} />
          )}
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <>
              <span className="hidden sm:inline text-sm text-slate-500">
                {user.name} · <span className="uppercase text-xs font-bold text-[#9b111e]">{user.role}</span>
              </span>
              <button
                data-testid="nav-logout-btn"
                onClick={() => { logout(); navigate("/"); }}
                className="btn-silver-3d !py-2 !px-4"
              >
                <LogOut className="w-4 h-4 text-[#9b111e]" />
                <span className="text-engraved text-sm">Logout</span>
              </button>
            </>
          ) : (
            <>
              <Link to="/auth?role=professional" data-testid="nav-apply-btn" className="btn-red-3d !py-2 !px-5 hidden sm:inline-flex">
                Apply Now
              </Link>
              <Link to="/auth" data-testid="nav-login-btn" className="btn-silver-3d !py-2 !px-5">
                <span className="text-engraved text-sm">Login</span>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
